import ViewCollection from '@ckeditor/ckeditor5-ui/src/viewcollection'
import View from '@ckeditor/ckeditor5-ui/src/view'
import FocusCycler, { FocusableView } from '@ckeditor/ckeditor5-ui/src/focuscycler'
import KeystrokeHandler from '@ckeditor/ckeditor5-utils/src/keystrokehandler'
import FocusTracker from '@ckeditor/ckeditor5-utils/src/focustracker'
import Locale from '@ckeditor/ckeditor5-utils/src/locale'
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview'
import LabeledFieldView from '@ckeditor/ckeditor5-ui/src/labeledfield/labeledfieldview'
import { createLabeledInputText } from '@ckeditor/ckeditor5-ui/src/labeledfield/utils'
import submitHandler from '@ckeditor/ckeditor5-ui/src/bindings/submithandler'
import { InputTextView } from '@ckeditor/ckeditor5-ui'
import { t } from '@ulule/localize'

import checkIcon from '@ckeditor/ckeditor5-core/theme/icons/check.svg'
import cancelIcon from '@ckeditor/ckeditor5-core/theme/icons/cancel.svg'

import './theme/anchorForm.css'
import '@ckeditor/ckeditor5-ui/theme/components/responsive-form/responsiveform.css'

export class AnchorFormView extends View {
  focusTracker: FocusTracker
  keystrokes: KeystrokeHandler
  urlInputView: LabeledFieldView<InputTextView>
  saveButtonView: ButtonView
  cancelButtonView: ButtonView
  private _focusables: ViewCollection<FocusableView>
  private _focusCycler: FocusCycler

  constructor(locale: Locale) {
    super(locale)

    this.focusTracker = new FocusTracker()

    this.keystrokes = new KeystrokeHandler()

    this.urlInputView = this._createUrlInput()

    this.saveButtonView = this._createButton(t('Save'), checkIcon, 'ck-button-save')
    this.saveButtonView.type = 'submit'

    this.cancelButtonView = this._createButton(t('Cancel'), cancelIcon, 'ck-button-cancel', 'cancel')

    this._focusables = new ViewCollection()

    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        // Navigate form fields backwards using the Shift + Tab keystroke.
        focusPrevious: 'shift + tab',

        // Navigate form fields forwards using the Tab key.
        focusNext: 'tab',
      },
    })

    const classList = ['ck', 'ck-anchor-form', 'ck-responsive-form']

    this.setTemplate({
      tag: 'form',

      attributes: {
        class: classList,
        // https://github.com/ckeditor/ckeditor5-anchor/issues/90
        tabindex: '-1',
      },

      children: [this.urlInputView, this.saveButtonView, this.cancelButtonView],
    })
  }

  render() {
    super.render()

    submitHandler({
      view: this,
    })

    const childViews = [this.urlInputView, this.saveButtonView, this.cancelButtonView]

    childViews.forEach((view) => {
      // Register the view as focusable.
      this._focusables.add(view)

      // Register the view in the focus tracker.
      if (view.element) {
        this.focusTracker.add(view.element)
      }
    })

    // Start listening for the keystrokes coming from #element.
    this.keystrokes.listenTo(this.element as any)
  }

  focus() {
    this._focusCycler.focusFirst()
  }

  /**
   * Creates a labeled input view.
   */
  private _createUrlInput(): LabeledFieldView<InputTextView> {
    const locale = this.locale!

    const labeledInput = new LabeledFieldView<InputTextView>(locale, createLabeledInputText)

    labeledInput.label = t('Anchor name')

    return labeledInput
  }

  /**
   * Creates a button view.
   */
  _createButton(label: string, icon: string, className: string, eventName?: string): ButtonView {
    const button = new ButtonView(this.locale)

    button.set({
      label,
      icon,
      tooltip: true,
    })

    button.extendTemplate({
      attributes: {
        class: className,
      },
    })

    if (eventName) {
      button.delegate('execute').to(this, eventName)
    }

    return button
  }
}
